/**
 * The UTC offset (ms to add to a UTC instant to get the wall-clock time) of
 * `timeZone` at the given instant. Derived from `Intl` so it is DST-aware and
 * needs no date library.
 */
function tzOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - instant.getTime();
}

/** The UTC instant of a wall-clock time in `timeZone` (DST-aware). */
function zonedWallToUtc(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  s: number,
  ms: number,
  timeZone: string,
): Date {
  // Compute the offset at whole-second granularity — `formatToParts` carries no
  // sub-second precision, so the millisecond component must be applied *after*
  // the offset correction (otherwise `.999` bounds skew by ~1s).
  const guessUtc = Date.UTC(y, mo - 1, d, h, mi, s, 0);
  const offset1 = tzOffsetMs(new Date(guessUtc), timeZone);
  let base = guessUtc - offset1;
  // Re-check for a DST transition between the guess and the corrected instant.
  const offset2 = tzOffsetMs(new Date(base), timeZone);
  if (offset2 !== offset1) base = guessUtc - offset2;
  return new Date(base + ms);
}

/**
 * Converts a `YYYY-MM-DD` calendar date (as picked in a `<input type="date">`)
 * to the UTC instants bounding that day **in the viewer's time zone**, so a
 * date-range filter agrees with a grid that renders the same instants viewer-
 * local (via {@link formatDate}). Sending naive `…T00:00:00Z`/`…T23:59:59.999Z`
 * bounds instead would drop or include near-midnight rows for any non-UTC viewer
 * (the row's displayed calendar day and the filter's day would disagree).
 *
 * Returns half-inclusive-looking bounds `{ from, to }` as ISO instants: `from`
 * is the start of the local day, `to` is `23:59:59.999` of the local day (the
 * REQ list filter is inclusive on both ends).
 */
export function zonedDayBoundsUtc(dateYmd: string, timeZone: string): { from: string; to: string } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateYmd);
  if (!match) {
    // Defensive: an unexpected value falls back to naive UTC bounds.
    return { from: `${dateYmd}T00:00:00.000Z`, to: `${dateYmd}T23:59:59.999Z` };
  }
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  return {
    from: zonedWallToUtc(y, mo, d, 0, 0, 0, 0, timeZone).toISOString(),
    to: zonedWallToUtc(y, mo, d, 23, 59, 59, 999, timeZone).toISOString(),
  };
}
